import { z } from "zod";
import { OkDataSchema } from "./common.js";
import { AgentIdSchema, IsoDateTimeSchema, NonEmptyStringSchema } from "./primitives.js";

const CredentialTypeSchema = z.enum(["api_key", "oauth"]);
const CredentialStatusSchema = z.enum(["active", "needs_reconnection"]);
const CredentialScopeSchema = z.enum(["global", "agent"]);

export const CredentialMetaSchema = z
  .object({
    scope: CredentialScopeSchema,
    agentId: AgentIdSchema.optional(),
    namespace: z.string().min(1),
    key: z.string().min(1),
    handle: z.string().min(1),
    displayName: z.string().optional(),
    description: z.string(),
    type: CredentialTypeSchema,
    provider: z.string().optional(),
    status: CredentialStatusSchema,
    statusMessage: z.string().optional(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    lastUsedAt: IsoDateTimeSchema.optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.scope === "agent" && !value.agentId) {
      ctx.addIssue({
        code: "custom",
        path: ["agentId"],
        message: "agent-scoped credentials require agentId",
      });
    }
    if (value.scope === "global" && value.agentId) {
      ctx.addIssue({
        code: "custom",
        path: ["agentId"],
        message: "global credentials must not include agentId",
      });
    }
  });

const OAuthCredentialBundleSchema = z
  .object({
    access_token: NonEmptyStringSchema,
    refresh_token: z.string().optional(),
    expires_at: IsoDateTimeSchema.optional(),
    client_id: z.string().optional(),
    client_secret: z.string().optional(),
    token_type: z.string().optional(),
    scopes: z.array(z.string()).optional(),
    provider_account_id: z.string().optional(),
  })
  .strict();

const CredentialValueSchema = z.union([NonEmptyStringSchema, OAuthCredentialBundleSchema]);

export const CredentialListPayloadSchema = z.object({ agentId: AgentIdSchema.optional() }).strict();
export const CredentialListDataSchema = z
  .object({ credentials: z.array(CredentialMetaSchema) })
  .strict();
export const CredentialSetPayloadSchema = z
  .object({
    namespace: z.string().min(1),
    key: z.string().min(1),
    type: CredentialTypeSchema,
    value: CredentialValueSchema,
    description: z.string(),
    displayName: z.string().optional(),
    agentId: AgentIdSchema.optional(),
    provider: z.string().optional(),
  })
  .strict();
export const CredentialSetDataSchema = z.object({ credential: CredentialMetaSchema }).strict();
export const CredentialUpdatePayloadSchema = z
  .object({
    namespace: z.string().min(1),
    key: z.string().min(1),
    agentId: AgentIdSchema.optional(),
    description: z.string().optional(),
    displayName: z.string().optional(),
    value: CredentialValueSchema.optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (
      value.description === undefined &&
      value.displayName === undefined &&
      value.value === undefined
    ) {
      ctx.addIssue({
        code: "custom",
        message: "credential update requires at least one mutable field",
      });
    }
  });
export const CredentialUpdateDataSchema = CredentialSetDataSchema;
export const CredentialDeletePayloadSchema = z
  .object({
    namespace: z.string().min(1),
    key: z.string().min(1),
    agentId: AgentIdSchema.optional(),
  })
  .strict();
export const CredentialDeleteDataSchema = OkDataSchema;

export type CredentialMeta = z.infer<typeof CredentialMetaSchema>;
export type OAuthCredentialBundle = z.infer<typeof OAuthCredentialBundleSchema>;
